import { ChangeDetectionStrategy, Component, computed, effect, inject, output, signal } from '@angular/core';
import { CategoryStoreActions } from '@era/stores';
import { Store } from '@ngrx/store';
import { DeviceLinkSelection, isDeviceLinkSelectionValid } from '../../device-link/device-link-selection';
import { DeviceLinkSelectorComponent } from '../../device-link/device-link-selector/device-link-selector.component';
import { selectDeviceLinkChildren, selectDeviceLinkLocations } from '../../device-link/device-link.selectors';
import { PairingCodeContainerComponent } from '../pairing-code/pairing-code.container';

@Component({
  selector: 'era-pairing-request',
  templateUrl: './pairing-request.component.html',
  styleUrls: ['./pairing-request.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DeviceLinkSelectorComponent, PairingCodeContainerComponent],
})
export class PairingRequestComponent {
  readonly cancelled = output<boolean>();

  private readonly store = inject(Store);

  readonly locations = this.store.selectSignal(selectDeviceLinkLocations);
  readonly children = this.store.selectSignal(selectDeviceLinkChildren);

  readonly selection = signal<DeviceLinkSelection>({});

  public currentTab = 0;

  readonly pairingCategoryId = computed(() => this.selection().categoryId ?? 'default-brand');
  readonly selectionValid = computed(() =>
    isDeviceLinkSelectionValid(this.selection(), this.locations(), this.children())
  );

  constructor() {
    effect(() => {
      const locationCategoryId = this.selection().locationCategoryId;
      if (locationCategoryId) {
        this.store.dispatch(
          CategoryStoreActions.CategoryLoadActions.assignableChildrenRequested({ locationCategoryId })
        );
      }
    });
  }

  public get allowNext() {
    return this.currentTab !== 1 || this.selectionValid();
  }

  public updateSelection(selection: DeviceLinkSelection): void {
    this.selection.set(selection);
  }

  public nextWithoutSelection() {
    this.selection.set({});
    this.currentTab = 2;
  }

  public next() {
    if (this.currentTab < 2 && this.allowNext) {
      this.currentTab = this.currentTab + 1;
    }
  }

  public previous() {
    if (this.currentTab > 0) {
      this.currentTab = this.currentTab - 1;
    }
  }

  public cancel() {
    this.cancelled.emit(true);
  }
}
